<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { useClinicianDisciplineStore } from '@/store';
import CheckboxForm from '@/components/Forms/CheckboxForm';
import { PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface PropsInterface {
  clinicianId: number | undefined;
}

const props = defineProps<PropsInterface>();
const clinicianDisciplineStore = useClinicianDisciplineStore();
const { disciplines, loading } = storeToRefs(clinicianDisciplineStore);
const dirty = ref(false);
const toast = useToast();

const onSave = async () => {
  if (can([PERMISSIONS.CLINICIAN_SYNC_DISCIPLINES])) {
    const result = await clinicianDisciplineStore.saveDisciplines(props.clinicianId as number);

    if (result === 200) {
      toast.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'Disciplines has been saved',
        life: 3000,
      });
      dirty.value = false;
    }
  }
};

const onUpdate = (id: string, checked: boolean) => {
  if (can([PERMISSIONS.CLINICIAN_SYNC_DISCIPLINES])) {
    if (!dirty.value) {
      dirty.value = true;
    }
    clinicianDisciplineStore.updateDisciplineForClinician({ id, checked });
  }
};

const onCancel = () => {
  dirty.value = false;
  if (can([PERMISSIONS.CLINICIAN_DISCIPLINES_INDEX]))
    clinicianDisciplineStore.getDisciplines(props.clinicianId as number);
};

onMounted(() => {
  if (
    can([PERMISSIONS.CLINICIAN_DISCIPLINES_INDEX]) &&
    (!clinicianDisciplineStore.disciplineIDs || clinicianDisciplineStore.disciplineIDs.length === 0)
  ) {
    clinicianDisciplineStore.getDisciplines(props.clinicianId as number);
  }
});

onUnmounted(() => {
  clinicianDisciplineStore.$reset();
});
</script>

<template>
  <div class="flex-1 min-w-max mb-4 field">
    <CheckboxForm
      title="Disciplines"
      :items="disciplines"
      :update-item="onUpdate"
      :save="onSave"
      :loading="loading"
      :cancel="onCancel"
      :dirty="dirty"
      class-name="checkbox-form"
    >
      <div v-if="clinicianDisciplineStore.disciplineIDs?.length > 1" class="mt-4 mb-4">
        <label for="is_primary">Primary Discipline</label>
        <Dropdown
          id="is_primary"
          v-model="clinicianDisciplineStore.primaryId"
          :options="clinicianDisciplineStore.getDisciplinesForDropdown()"
          class="w-full mt-2"
          option-label="name"
          option-value="id"
          placeholder="Select a primary discipline"
          :show-clear="true"
          @change="onUpdate(id, false)"
        ></Dropdown>
      </div>
    </CheckboxForm>
  </div>
</template>

<style scoped lang="scss"></style>
